import { useMutation, useQuery, useQueryClient, keepPreviousData, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiError, core, type Paged } from './client';

export function useGet<T = any>(path: string | null, params?: Record<string, unknown>, opts?: Partial<UseQueryOptions<T>>) {
  return useQuery<T>({
    queryKey: [path, params],
    queryFn: async () => (await core.get<T>(path!, { params: clean(params) })).data,
    enabled: !!path,
    placeholderData: keepPreviousData,
    ...opts,
  });
}

export function usePaged<T = any>(path: string, params: Record<string, unknown>) {
  return useGet<Paged<T>>(path, params);
}

/** POST/PATCH/PUT/DELETE with toast + automatic cache invalidation of related lists. */
export function useAction<TBody = any, TOut = any>(
  method: 'post' | 'patch' | 'put' | 'delete',
  path: string | ((b: TBody) => string),
  opts: { success?: string | ((out: TOut) => string); invalidate?: string[]; onSuccess?: (out: TOut) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation<TOut, unknown, TBody>({
    mutationFn: async (body: TBody) => {
      const url = typeof path === 'function' ? path(body) : path;
      const r = method === 'delete' ? await core.delete<TOut>(url) : await core[method]<TOut>(url, body);
      return r.data;
    },
    onSuccess: (out) => {
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(out) : opts.success);
      for (const prefix of opts.invalidate ?? ['/']) {
        qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith(prefix) });
      }
      opts.onSuccess?.(out);
    },
    onError: (e) => toast.error(apiError(e)),
  });
}

function clean(p?: Record<string, unknown>) {
  if (!p) return undefined;
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== '' && v !== null));
}
